import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { Button, Badge, Modal, FormInput, ConfirmDialog } from '../components/ui';
import StakeTable, { Column } from './GameEngine/StakeTable';
import { toast } from 'react-toastify';
import { UserPlus, Trash2, Edit, ShieldAlert, CheckSquare, Square } from 'lucide-react';

interface Role {
  id: number;
  name: string;
  permissions: Record<string, boolean>;
}

interface TeamMember {
  id: number;
  username: string;
  email: string;
  role_id: number | null;
  role_name: string | null;
  custom_permissions: Record<string, boolean>;
  is_active: boolean;
}

const AVAILABLE_PERMISSIONS = [
  { category: 'General', key: 'dashboard', label: 'Access Dashboard' },
  { category: 'General', key: 'users', label: 'Manage Users & Profiles' },
  { category: 'General', key: 'notifications', label: 'Manage Notifications' },

  { category: 'Team Management', key: 'roles', label: 'Role Management' },
  { category: 'Team Management', key: 'team', label: 'Team Members' },

  { category: 'Offers Management', key: 'campaigns', label: 'Campaign Builder' },
  { category: 'Offers Management', key: 'affiliate_settings', label: 'Affiliate Settings' },
  { category: 'Offers Management', key: 'vip_levels', label: 'VIP Levels' },

  { category: 'Manage Payment', key: 'withdrawals', label: 'Withdrawal' },
  { category: 'Manage Payment', key: 'deposits', label: 'Deposit' },

  { category: 'CMS Management', key: 'cms_pages', label: 'Pages Content' },
  { category: 'CMS Management', key: 'cms_banners', label: 'Sliders & Banners' },
  { category: 'CMS Management', key: 'cms_logos', label: 'Logos & Branding' },
  { category: 'CMS Management', key: 'cms_footer', label: 'Footer Settings' },
  { category: 'CMS Management', key: 'cms_social', label: 'Social Links' },
  { category: 'CMS Management', key: 'cms_help', label: 'Help Center' },
  { category: 'CMS Management', key: 'cms_game_cards', label: 'Home Game Cards' },
  { category: 'CMS Management', key: 'cms_affiliate', label: 'Affiliate CMS' },

  { category: 'Analytics & Reports', key: 'reports_users', label: 'User Reports' },
  { category: 'Analytics & Reports', key: 'reports_financial', label: 'Financial & Bonus Logs' },
  { category: 'Analytics & Reports', key: 'reports_games', label: 'Global Game History' },
  { category: 'Analytics & Reports', key: 'reports_profit', label: 'GGR & Analytics' },

  { category: 'Settings', key: 'settings_general', label: 'Website Setup' },
  { category: 'Settings', key: 'settings_auth', label: 'Auth Requirements' },
  { category: 'Settings', key: 'settings_gateway', label: 'Gateway Keys' },

  { category: 'Game Engine', key: 'game_engine_aggregators', label: 'Aggregators' },
  { category: 'Game Engine', key: 'game_engine_providers', label: 'Providers' },
  { category: 'Game Engine', key: 'game_engine_categories', label: 'Categories' },
  { category: 'Game Engine', key: 'game_engine_groups', label: 'Groups' },
  { category: 'Game Engine', key: 'game_engine_games', label: 'Games' },
];

const TeamManagement: React.FC = () => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showOverridesModal, setShowOverridesModal] = useState(false);
  const [deleteMemberId, setDeleteMemberId] = useState<number | null>(null);

  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [formData, setFormData] = useState({ username: '', email: '', password: '', role_id: '' });

  const [overrideMember, setOverrideMember] = useState<TeamMember | null>(null);
  const [selectedOverrides, setSelectedOverrides] = useState<Record<string, boolean>>({});

  // Pagination & Filtering state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRoleId, setFilterRoleId] = useState('');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchMembers();
  }, [page, limit, searchTerm, filterStatus, filterRoleId]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchMembers = async () => {
    setIsLoading(true);
    try {
      const offset = (page - 1) * limit;
      let url = `/collaborator/team?limit=${limit}&offset=${offset}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterRoleId) url += `&role_id=${filterRoleId}`;
      const res = await apiClient.get(url);
      if (res.data.status) {
        setMembers(res.data.data || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load team members.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await apiClient.get('/collaborator/roles');
      if (res.data.status) setRoles(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenCreate = () => {
    setEditingMember(null);
    setFormData({ username: '', email: '', password: '', role_id: '' });
    setShowMemberModal(true);
  };

  const handleUpdateRoleDirectly = async (memberId: number, roleId: string) => {
    try {
      const res = await apiClient.put(`/collaborator/team/${memberId}`, {
        role_id: roleId ? Number(roleId) : null
      });
      if (res.data.status) {
        toast.success('Role updated successfully.');
        fetchMembers();
      }
    } catch (err) {
      console.error('Failed to update role:', err);
      toast.error('Failed to update role.');
    }
  };

  const handleOpenEdit = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({
      username: member.username,
      email: member.email,
      password: '',
      role_id: member.role_id ? String(member.role_id) : ''
    });
    setShowMemberModal(true);
  };

  const handleSaveMember = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: any = {
      username: formData.username,
      email: formData.email,
      role_id: formData.role_id ? Number(formData.role_id) : null
    };
    if (formData.password) payload.password = formData.password;

    try {
      if (editingMember) {
        const res = await apiClient.put(`/collaborator/team/${editingMember.id}`, payload);
        if (res.data.status) {
          toast.success('Team member updated.');
          fetchMembers();
        }
      } else {
        const res = await apiClient.post('/collaborator/team', { ...payload, password: formData.password });
        if (res.data.status) {
          toast.success('Team member created.');
          setPage(1);
          fetchMembers();
        }
      }
      setShowMemberModal(false);
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to save team member.');
    }
  };

  const handleToggleStatus = async (member: TeamMember) => {
    const nextStatus = !member.is_active;
    try {
      const res = await apiClient.put(`/collaborator/team/${member.id}`, { is_active: nextStatus });
      if (res.data.status) {
        toast.success(`Member is now ${nextStatus ? 'active' : 'suspended'}.`);
        fetchMembers();
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to change status.');
    }
  };

  const handleConfirmDeleteMember = async () => {
    if (!deleteMemberId) return;
    try {
      const res = await apiClient.delete(`/collaborator/team/${deleteMemberId}`);
      if (res.data.status) {
        toast.success('Member removed.');
        fetchMembers();
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete member.');
    } finally {
      setDeleteMemberId(null);
    }
  };

  const handleOpenOverrides = (member: TeamMember) => {
    setOverrideMember(member);
    setSelectedOverrides(member.custom_permissions || {});
    setShowOverridesModal(true);
  };

  const handleToggleOverride = (key: string, hasRoleAccess: boolean) => {
    setSelectedOverrides(prev => {
      const copy = { ...prev };
      if (copy[key] !== undefined) {
        delete copy[key];
      } else {
        copy[key] = !hasRoleAccess;
      }
      return copy;
    });
  };

  const handleSaveOverrides = async () => {
    if (!overrideMember) return;
    try {
      const res = await apiClient.put(`/collaborator/team/${overrideMember.id}`, { custom_permissions: selectedOverrides });
      if (res.data.status) {
        toast.success('Permission overrides updated.');
        fetchMembers();
        setShowOverridesModal(false);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save permission overrides.');
    }
  };

  const columns: Column[] = [
    { key: 'sn', label: 'S.No.', render: (_, __, i) => (page - 1) * limit + i + 1 },
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'Email' },
    {
      key: 'role_id',
      label: 'Default Role',
      render: (val, item: TeamMember) => (
        <select
          className="bg-[#0f172a] border border-slate-800 hover:border-slate-700 rounded px-2 py-1 text-[#f1f5f9] text-xs outline-none cursor-pointer transition-colors"
          value={val ? String(val) : ''}
          onChange={(e) => handleUpdateRoleDirectly(item.id, e.target.value)}
        >
          <option value="" className="text-slate-400">No Role</option>
          {roles.map(r => (
            <option key={r.id} value={r.id} className="text-slate-200">{r.name}</option>
          ))}
        </select>
      )
    },
    {
      key: 'custom_permissions',
      label: 'Custom Overrides',
      render: (val) => {
        const overrides = Object.keys(val || {}).filter(k => val[k]);
        if (overrides.length === 0) return <span className="text-slate-500 text-xs italic">None</span>;
        return <Badge variant="warning">{overrides.length} Custom Overrides</Badge>;
      }
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (val) => val
        ? <Badge variant="active">Active</Badge>
        : <Badge variant="danger">Suspended</Badge>
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 'w-[360px]',
      render: (_, item: TeamMember) => (
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(item)}>
            <Edit size={13} /> Edit
          </Button>
          <Button size="sm" variant="warning" onClick={() => handleOpenOverrides(item)}>
            <ShieldAlert size={13} /> Overrides
          </Button>
          <Button size="sm" variant={item.is_active ? 'warning' : 'success'} onClick={() => handleToggleStatus(item)}>
            {item.is_active ? 'Suspend' : 'Activate'}
          </Button>
          <Button size="sm" variant="danger" onClick={() => setDeleteMemberId(item.id)}>
            <Trash2 size={13} /> Remove
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="ml-0 lg:ml-[260px] p-4 sm:p-6 text-white min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white">Team & Members</h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Manage back-office members, assign roles, and configure per-user permission overrides.
          </p>
        </div>
      </div>

      <StakeTable
        title=""
        actions={
          <Button onClick={handleOpenCreate} className="flex items-center justify-center gap-2 shrink-0">
            <UserPlus size={16} /> Add Team Member
          </Button>
        }
        columns={columns}
        data={members}
        isLoading={isLoading}
        emptyMessage="No team members yet. Click 'Add Team Member' to get started."
        hasSidebarMargin={false}
        page={page}
        totalPages={Math.ceil(total / limit)}
        total={total}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        onSearch={(val) => { setSearchTerm(val); setPage(1); }}
        searchPlaceholder="Search team members..."
        bottomActions={
          <div className="flex flex-wrap items-center gap-2.5 w-full">
            <select
              className="bg-slate-900 border border-slate-700 text-slate-300 text-xs sm:text-sm rounded-md shadow-sm hover:bg-slate-800 focus:outline-none focus:border-indigo-500 py-1.5 sm:py-2 px-2.5 sm:px-3 transition-colors cursor-pointer flex-1 sm:flex-initial min-w-[110px]"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            >
              <option value="" className="bg-slate-900 text-slate-400">All Statuses</option>
              <option value="active" className="bg-slate-900 text-slate-200">Active</option>
              <option value="suspended" className="bg-slate-900 text-slate-200">Suspended</option>
            </select>

            <select
              className="bg-slate-900 border border-slate-700 text-slate-300 text-xs sm:text-sm rounded-md shadow-sm hover:bg-slate-800 focus:outline-none focus:border-indigo-500 py-1.5 sm:py-2 px-2.5 sm:px-3 transition-colors cursor-pointer flex-1 sm:flex-initial min-w-[110px]"
              value={filterRoleId}
              onChange={(e) => { setFilterRoleId(e.target.value); setPage(1); }}
            >
              <option value="" className="bg-slate-900 text-slate-400">All Roles</option>
              {roles.map(r => (
                <option key={r.id} value={r.id} className="bg-slate-900 text-slate-200">{r.name}</option>
              ))}
            </select>
          </div>
        }
      />

      {/* Member Form Modal */}
      <Modal
        open={showMemberModal}
        title={editingMember ? 'Edit Team Member' : 'Add Team Member'}
        onClose={() => setShowMemberModal(false)}
      >
        <form onSubmit={handleSaveMember} className="flex flex-col gap-4">
          <FormInput
            label="Username"
            placeholder="e.g. support_jack"
            value={formData.username}
            onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
            required
          />
          <FormInput
            label="Email Address"
            type="email"
            placeholder="jack@domain.com"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            required
          />
          <FormInput
            label={editingMember ? 'Password (leave blank to keep unchanged)' : 'Password'}
            type="password"
            placeholder="Enter password"
            value={formData.password}
            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
            required={!editingMember}
          />

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-slate-300 mb-2">
              Default Role
            </label>
            <select
              className="w-full bg-[#0f172a] border border-slate-800 hover:border-slate-700 rounded-lg p-2.5 text-[#f1f5f9] text-xs sm:text-sm outline-none cursor-pointer transition-colors"
              value={formData.role_id}
              onChange={(e) => setFormData(prev => ({ ...prev, role_id: e.target.value }))}
            >
              <option value="" className="text-slate-400">Select a default role...</option>
              {roles.map(r => (
                <option key={r.id} value={r.id} className="text-slate-200">{r.name}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
            <Button type="button" variant="outline" onClick={() => setShowMemberModal(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {editingMember ? 'Save Changes' : 'Create Member'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Overrides Modal */}
      <Modal
        open={showOverridesModal}
        title={overrideMember ? `Custom Overrides: ${overrideMember.username}` : 'Custom Overrides'}
        onClose={() => setShowOverridesModal(false)}
      >
        <div className="flex flex-col gap-5">
          <p className="color-[#94a3b8] text-xs sm:text-sm text-slate-400 leading-relaxed">
            Toggle permissions to force grant or deny access for this specific member, overriding their default role.
          </p>

          <div className="flex items-center justify-between border-b border-slate-800 pb-2">
            <label className="block text-xs sm:text-sm font-semibold text-slate-300">
              Custom Overrides Matrix
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const updated: Record<string, boolean> = {};
                  AVAILABLE_PERMISSIONS.forEach(p => { updated[p.key] = true; });
                  setSelectedOverrides(updated);
                }}
                className="text-xs text-indigo-400 hover:text-indigo-300 font-semibold cursor-pointer"
              >
                Select All
              </button>
              <span className="text-slate-600 text-xs">|</span>
              <button
                type="button"
                onClick={() => {
                  const updated: Record<string, boolean> = {};
                  AVAILABLE_PERMISSIONS.forEach(p => { updated[p.key] = false; });
                  setSelectedOverrides(updated);
                }}
                className="text-xs text-rose-400 hover:text-rose-300 font-semibold cursor-pointer"
              >
                Deselect All
              </button>
              <span className="text-slate-600 text-xs">|</span>
              <button
                type="button"
                onClick={() => setSelectedOverrides({})}
                className="text-xs text-slate-400 hover:text-slate-300 font-semibold cursor-pointer"
              >
                Reset to Default
              </button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto pr-2 flex flex-col gap-4 custom-thin-scrollbar">
            {Object.entries(
              AVAILABLE_PERMISSIONS.reduce((acc, p) => {
                if (!acc[p.category]) acc[p.category] = [];
                acc[p.category].push(p);
                return acc;
              }, {} as Record<string, typeof AVAILABLE_PERMISSIONS>)
            ).map(([category, perms]) => (
              <div key={category} className="flex flex-col gap-2">
                <div className="flex items-center justify-between border-b border-slate-800 pb-1">
                  <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-wider">{category}</h4>
                  <div className="flex gap-2 text-[10px]">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedOverrides(prev => {
                          const copy = { ...prev };
                          perms.forEach(p => { copy[p.key] = true; });
                          return copy;
                        });
                      }}
                      className="text-emerald-400 hover:text-emerald-300 font-semibold cursor-pointer"
                    >
                      All
                    </button>
                    <span className="text-slate-600">|</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedOverrides(prev => {
                          const copy = { ...prev };
                          perms.forEach(p => { delete copy[p.key]; });
                          return copy;
                        });
                      }}
                      className="text-slate-400 hover:text-slate-300 font-semibold cursor-pointer"
                    >
                      None
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {perms.map(p => {
                    const memberRole = roles.find(r => r.id === overrideMember?.role_id);
                    const hasRoleAccess = !!memberRole?.permissions?.[p.key];
                    const overrideVal = selectedOverrides[p.key];
                    const isCurrentlyActive = overrideVal !== undefined ? overrideVal : hasRoleAccess;
                    
                    let bgBorderClass = 'bg-slate-900/50 border-slate-800 text-slate-400 hover:bg-slate-800/50 hover:text-slate-200';
                    let badge = null;

                    if (isCurrentlyActive) {
                      if (overrideVal === true) {
                        bgBorderClass = 'bg-amber-500/10 border-amber-500/30 text-amber-400';
                        badge = <span className="text-[9px] font-bold text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20 ml-auto shrink-0">Override Granted</span>;
                      } else {
                        bgBorderClass = 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400';
                        badge = <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 ml-auto shrink-0">Role Default</span>;
                      }
                    } else {
                      if (overrideVal === false) {
                        bgBorderClass = 'bg-rose-500/10 border-rose-500/30 text-rose-450';
                        badge = <span className="text-[9px] font-bold text-rose-450 bg-rose-500/10 px-1.5 py-0.5 rounded border border-rose-500/20 ml-auto shrink-0">Override Denied</span>;
                      }
                    }

                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => handleToggleOverride(p.key, hasRoleAccess)}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border text-left cursor-pointer transition-all duration-150 ${bgBorderClass}`}
                      >
                        {isCurrentlyActive ? <CheckSquare size={16} /> : <Square size={16} />}
                        <span className="text-xs font-semibold">{p.label}</span>
                        {badge}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
            <Button type="button" variant="outline" onClick={() => setShowOverridesModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveOverrides}>
              Save Overrides
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteMemberId !== null}
        onClose={() => setDeleteMemberId(null)}
        onConfirm={handleConfirmDeleteMember}
        title="Delete Team Member"
        message="Delete this team member? This action is permanent."
        confirmLabel="Remove"
        confirmVariant="danger"
      />
    </div>
  );
};

export default TeamManagement;
